import { useMemo, useRef, useState } from "react";
import { Check, ChevronDown, ChevronRight, TriangleAlert } from "lucide-react";

import type { Cell, Grid, GridLine, GridSection } from "@/lib/budget";
import { money } from "@/lib/format";
import { tagStyle } from "@/lib/meta";
import { cn } from "@/lib/utils";

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Compact money for the grid, where twelve columns have to fit. */
function short(n: number): string {
  if (n === 0) return "—";
  return new Intl.NumberFormat("pl-PL", { maximumFractionDigits: 0 }).format(n);
}

interface Props {
  grid: Grid;
  /** month is 1-based */
  onSetCell: (lineId: string, month: number, amount: number | null, status?: "pending" | "done") => Promise<void>;
  onClearCell: (lineId: string, month: number) => Promise<void>;
}

/**
 * A year of the budget, for recording what was actually paid. The lines
 * themselves are defined on the budget page; nothing here creates or removes
 * one, so a month-end pass cannot reshape the budget by accident.
 */
export function BudgetGrid({ grid, onSetCell, onClearCell }: Props) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const inputs = useRef(new Map<string, HTMLInputElement>());

  const editableOrder = useMemo(
    () =>
      grid.sections
        .filter((s) => s.kind !== "formula")
        .flatMap((s) => (collapsed[s.label] ? [] : s.lines.map((l) => l.id))),
    [grid.sections, collapsed],
  );

  const now = new Date();
  const lastRelevantMonth =
    grid.year < now.getFullYear() ? 12 : grid.year > now.getFullYear() ? 0 : now.getMonth() + 1;

  const key = (lineId: string, month: number) => `${lineId}:${month}`;

  const focusCell = (lineId: string, month: number) => {
    inputs.current.get(key(lineId, month))?.focus();
    inputs.current.get(key(lineId, month))?.select();
  };

  const moveDown = (lineId: string, month: number) => {
    const i = editableOrder.indexOf(lineId);
    const next = editableOrder[i + 1];
    if (next) focusCell(next, month);
  };

  const commit = async (line: GridLine, month: number, raw: string) => {
    const k = key(line.id, month);
    const cell = line.cells[month - 1] ?? null;
    const stored = cell?.amount ?? null;
    const trimmed = raw.trim().replace(",", ".");

    setDrafts((d) => {
      const { [k]: _drop, ...rest } = d;
      return rest;
    });

    if (trimmed === "") {
      if (cell) await onClearCell(line.id, month);
      return;
    }
    const amount = Number(trimmed);
    if (Number.isNaN(amount) || amount === stored) return;
    await onSetCell(line.id, month, amount);
  };

  const toggleDone = async (line: GridLine, month: number) => {
    const cell = line.cells[month - 1] ?? null;
    if (!cell) {
      await onSetCell(line.id, month, null, "done");
      return;
    }
    if (cell.status === "done" && cell.amount === null) {
      await onClearCell(line.id, month);
      return;
    }
    await onSetCell(line.id, month, cell.amount, cell.status === "done" ? "pending" : "done");
  };

  return (
    <div className="space-y-2">
      <div className="max-h-[calc(100vh-17rem)] min-h-64 overflow-auto rounded-lg border bg-background md:max-h-[calc(100vh-14rem)]">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b bg-muted">
              <th className="sticky left-0 top-0 z-40 min-w-44 border-r bg-muted px-3 py-2 text-left font-medium">
                Line
              </th>
              {MONTHS.map((m, i) => (
                <th
                  key={m}
                  className={cn(
                    "sticky top-0 z-30 min-w-16 bg-muted px-2 py-2 text-right font-medium tabular-nums",
                    i + 1 === lastRelevantMonth && "text-primary",
                  )}
                >
                  {m}
                </th>
              ))}
              <th className="sticky top-0 z-30 min-w-20 bg-muted px-3 py-2 text-right font-semibold">Year</th>
            </tr>
          </thead>

          <tbody>
            {grid.sections.map((section) => (
              <SectionRows
                key={section.label}
                section={section}
                collapsed={!!collapsed[section.label]}
                onToggle={() => setCollapsed((c) => ({ ...c, [section.label]: !c[section.label] }))}
                lastRelevantMonth={lastRelevantMonth}
                drafts={drafts}
                setDrafts={setDrafts}
                inputs={inputs}
                cellKey={key}
                commit={commit}
                toggleDone={toggleDone}
                moveDown={moveDown}
              />
            ))}
          </tbody>

          <tfoot className="border-t-2">
            <TotalRow label="Income" values={grid.totals.income} />
            <TotalRow label="Expenses" values={grid.totals.expenses} />
            <TotalRow label="Balance" values={grid.totals.balance} strong />
          </tfoot>
        </table>
      </div>
    </div>
  );
}

interface SectionProps {
  section: GridSection;
  collapsed: boolean;
  onToggle: () => void;
  lastRelevantMonth: number;
  drafts: Record<string, string>;
  setDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  inputs: React.RefObject<Map<string, HTMLInputElement>>;
  cellKey: (lineId: string, month: number) => string;
  commit: (line: GridLine, month: number, raw: string) => Promise<void>;
  toggleDone: (line: GridLine, month: number) => Promise<void>;
  moveDown: (lineId: string, month: number) => void;
}

function SectionRows({
  section,
  collapsed,
  onToggle,
  lastRelevantMonth,
  drafts,
  setDrafts,
  inputs,
  cellKey,
  commit,
  toggleDone,
  moveDown,
}: SectionProps) {
  const isFormula = section.kind === "formula";

  return (
    <>
      <tr className="border-b bg-muted/20">
        {/*
          The cell spans every column, so it cannot itself slide within its row:
          a sticky cell is constrained by its containing block, and here the two
          are the same width. Pinning the label inside it keeps the section name
          on screen once the months are scrolled.
        */}
        <th colSpan={14} className="p-0 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <button
            onClick={onToggle}
            className="sticky left-0 z-10 flex items-center gap-1.5 px-3 py-1.5 hover:text-foreground"
          >
            {collapsed ? <ChevronRight className="size-3.5" /> : <ChevronDown className="size-3.5" />}
            {section.tag && <span className={cn("size-2 rounded-full", tagStyle(section.tag.color).dot)} />}
            {section.label}
          </button>
        </th>
      </tr>

      {!collapsed &&
        section.lines.map((line) => (
          <tr key={line.id} className="border-b last:border-0 hover:bg-muted/20">
            <td className="sticky left-0 z-20 max-w-56 truncate border-r bg-background px-3 py-1.5">
              <span className="font-medium">{line.name}</span>
              {line.formulaText && (
                <span className="ml-2 font-mono text-[11px] text-muted-foreground">{line.formulaText}</span>
              )}
              {line.error && (
                <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-destructive" title={line.error}>
                  <TriangleAlert className="size-3" />
                </span>
              )}
            </td>

            {MONTHS.map((_, i) => {
              const month = i + 1;
              if (isFormula) {
                const v = line.values?.[i] ?? 0;
                return (
                  <td key={month} className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                    {line.error ? "—" : short(v)}
                  </td>
                );
              }
              return (
                <EditableCell
                  key={month}
                  line={line}
                  month={month}
                  cell={line.cells[i] ?? null}
                  plan={line.plans?.[i] ?? null}
                  overdue={month <= lastRelevantMonth}
                  draft={drafts[cellKey(line.id, month)]}
                  setDraft={(v) => setDrafts((d) => ({ ...d, [cellKey(line.id, month)]: v }))}
                  register={(el) => {
                    if (el) inputs.current?.set(cellKey(line.id, month), el);
                    else inputs.current?.delete(cellKey(line.id, month));
                  }}
                  commit={commit}
                  toggleDone={toggleDone}
                  moveDown={moveDown}
                />
              );
            })}

            <td className="px-3 py-1.5 text-right font-medium tabular-nums">{short(line.yearTotal)}</td>
          </tr>
        ))}

      {!collapsed && !isFormula && (
        <tr className="border-b bg-muted text-xs">
          <td className="sticky left-0 z-20 border-r bg-muted px-3 py-1 text-right font-semibold text-muted-foreground">
            Σ {section.label}
          </td>
          {section.subtotals.map((v, i) => (
            <td key={i} className="px-2 py-1 text-right font-semibold tabular-nums">
              {short(v)}
            </td>
          ))}
          <td className="px-3 py-1 text-right font-semibold tabular-nums">
            {short(section.subtotals.reduce((a, b) => a + b, 0))}
          </td>
        </tr>
      )}
    </>
  );
}

function EditableCell({
  line,
  month,
  cell,
  plan,
  overdue,
  draft,
  setDraft,
  register,
  commit,
  toggleDone,
  moveDown,
}: {
  line: GridLine;
  month: number;
  cell: Cell | null;
  plan: number | null;
  overdue: boolean;
  draft: string | undefined;
  setDraft: (v: string) => void;
  register: (el: HTMLInputElement | null) => void;
  commit: (line: GridLine, month: number, raw: string) => Promise<void>;
  toggleDone: (line: GridLine, month: number) => Promise<void>;
  moveDown: (lineId: string, month: number) => void;
}) {
  const stored = cell?.amount != null ? String(cell.amount) : "";
  const value = draft ?? stored;
  const done = cell?.status === "done";
  const pending = !cell && overdue;

  return (
    <td
      className={cn(
        "relative border-l p-0",
        pending && "bg-amber-50 dark:bg-amber-950/30",
        done && "bg-emerald-50/60 dark:bg-emerald-950/20",
        cell?.status === "pending" && "bg-muted/40",
      )}
    >
      <input
        ref={register}
        inputMode="decimal"
        aria-label={`${line.name} ${MONTHS[month - 1]}`}
        className="w-full bg-transparent px-2 py-1.5 text-right tabular-nums outline-none focus:bg-background focus:ring-2 focus:ring-inset focus:ring-primary"
        value={value}
        placeholder={plan != null ? String(plan) : ""}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={(e) => void commit(line, month, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void commit(line, month, e.currentTarget.value);
            moveDown(line.id, month);
          } else if (e.key === "Escape") {
            e.preventDefault();
            setDraft(stored);
            e.currentTarget.blur();
          }
          // Tab is left alone: the next tabbable element is the next month's
          // input, which is exactly "move right". The tick button is skipped
          // (tabIndex -1) so it cannot interrupt that motion.
        }}
        onKeyDownCapture={(e) => {
          // Keyboard route to the tick, since it is out of the tab order.
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void toggleDone(line, month);
          }
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={done ? `Mark ${line.name} ${MONTHS[month - 1]} pending` : `Mark ${line.name} ${MONTHS[month - 1]} done`}
        onClick={() => void toggleDone(line, month)}
        className={cn(
          "absolute left-0.5 top-0.5 rounded p-0.5 transition-opacity",
          done ? "text-emerald-600 opacity-100" : "text-muted-foreground opacity-0 hover:opacity-100 focus:opacity-100",
        )}
      >
        <Check className="size-3" />
      </button>
    </td>
  );
}

function TotalRow({ label, values, strong }: { label: string; values: number[]; strong?: boolean }) {
  return (
    <tr className={cn("border-t", strong && "bg-muted")}>
      <td className={cn("sticky left-0 z-20 border-r bg-background px-3 py-2 font-semibold", strong && "bg-muted")}>
        Σ {label}
      </td>
      {values.map((v, i) => (
        <td
          key={i}
          className={cn("px-2 py-2 text-right font-semibold tabular-nums", strong && v < 0 && "text-destructive")}
        >
          {short(v)}
        </td>
      ))}
      <td className="px-3 py-2 text-right font-bold tabular-nums" title={money(values.reduce((a, b) => a + b, 0))}>
        {short(values.reduce((a, b) => a + b, 0))}
      </td>
    </tr>
  );
}
